import { Injectable } from "@angular/core";

/**
 * @fileOverview Role-based access control configuration and feature mapping
 * @description Centralized configuration service that defines role-to-feature mappings,
 * subscription policies, and feature availability rules. Manages RBAC policies and
 * provides lookup methods for determining feature accessibility based on user roles
 * and active subscription plans.
 *
 * @module roleAccessControlConfig
 */

/**
 * @interface SubscriptionRule
 * @description Represents a subscription rule linking a policy flag to unlocked features.
 * Used internally to map subscription plans to feature sets, enabling dynamic feature
 * availability based on active subscriptions.
 *
 * @example
 * const rule: SubscriptionRule = {
 *   flag: 'LITE',
 *   features: new Set(['DASHBOARD', 'POND', 'FEED'])
 * };
 */
export interface SubscriptionRule {
    /**
     * @type {string}
     * @description The unique flag identifier for a subscription plan/policy.
     * Corresponds to policy enum values (e.g., 'LITE', 'CORE', 'PREMIUM', 'PBC', 'PPP', 'BELP')
     */
    flag: string;

    /**
     * @type {Set<string>}
     * @description Set of feature IDs that are unlocked by this subscription flag.
     * Each string represents a unique feature identifier that can be accessed
     * when this subscription policy is active.
     */
    features: Set<string>;
}

/**
 * @enum Roles
 * @description Enumeration of all user roles in the application, representing different
 * user types with varying access levels and responsibilities.
 *
 * Role Hierarchy & Access Patterns:
 * - EMPLOYEE: Minimal access, system-wide operational features only
 * - FARM roles: Farm business operations (FARM_BUSINESS_ADMIN, FARMER, FARM_MANAGER)
 * - PROVIDER roles: Provider business operations (PROVIDER_BUSINESS_ADMIN, PROVIDER_MANAGER)
 * - Super Admin: Unrestricted access (mapped from EMPLOYEE in specific contexts)
 *
 * @type {enum}
 * @example
 * import { Roles } from './roleAccessControlConfig.service';
 * const userRole = Roles.FARM_BUSINESS_ADMIN;
 * const features = config.getDefaultFeaturesForRole(userRole);
 */
export enum Roles {
    /**
     * Regular employee role.
     * Used for system administrators with broad cross-cutting access.
     */
    EMPLOYEE = "EMPLOYEE",

    /**
     * Farm Business administrator with full access to business operations.
     * Highest farm business role with management capabilities.
     */
    FARM_BUSINESS_ADMIN = "Farm Business Admin",

    /**
     * Owner of a farm.
     * Farm owner/proprietor role.
     */
    FARMER = "Farmer",

    /**
     * Manager responsible for farm operations.
     * Operations manager for farm activities.
     */
    FARM_MANAGER = "Farm Manager",

    /**
     * Provider Business administrator with full access to business operations.
     * Highest provider business role with management capabilities.
     */
    PROVIDER_BUSINESS_ADMIN = "Provider Business Admin",

    /**
     * Manager responsible for Provider business operations.
     * Operations manager for provider activities.
     */
    PROVIDER_MANAGER = "Provider Manager",

    /** Customer support role, handling customer queries and support. */
    CUSTOMER_SUPPORT = "Customer Support",
}

/**
 * @enum Policies
 * @description Enumeration of subscription plans/policies available in the system.
 * Each policy defines a tier of features and capabilities available to subscribed users.
 *
 * Plan Tiers (ascending feature availability):
 * - TRIAL (TBC): Trial/evaluation plan with limited features
 * - LITE: Entry-level plan with basic features
 * - CORE: Mid-tier plan with standard features
 * - PREMIUM (PBC): Premium tier with enhanced features
 * - PRIORITY_PREMIUM (PPP): Top-tier premium with priority support and exclusive features
 * - BLUEYE (BELP): Specialized monitoring/analytics plan
 *
 * @type {enum}
 * @example
 * import { Policies } from './roleAccessControlConfig.service';
 * const userPlans = [Policies.LITE, Policies.CORE];
 * const features = config.getFeaturesByActivePolicyFlags([...userPlans]);
 */
export enum Policies {
    /** Trial/evaluation plan (TBC - To Be Confirmed). Limited feature access for evaluation. */
    TRIAL = 'TBC',

    /** Lite plan. Entry-level subscription with basic operational features. */
    LITE = 'LITE',

    /** Core plan. Standard subscription with standard feature set. */
    CORE = 'CORE',

    /** Premium plan (PBC - Premium Basic Core). Full-featured premium subscription. */
    PREMIUM = 'PBC',

    /** Priority Premium plan (PPP). Top-tier subscription with priority support and exclusive features. */
    PRIORITY_PREMIUM = 'PPP',

    /** BluEye monitoring plan (BELP - BluEye Lite Plan). Specialized analytics and monitoring features. */
    BLUEYE = 'BELP',
}

@Injectable({
    providedIn: 'root',
})
/**
 * @service AccessControlConfigService
 * @description Service that manages and provides access to role-based access control (RBAC)
 * configuration and feature mappings. Acts as the single source of truth for:
 * - Role-to-feature mappings (which features each role can access)
 * - Subscription plan-to-feature mappings (which features each subscription unlocks)
 * - Feature availability based on user roles and active subscriptions
 *
 * Core Responsibilities:
 * - Initialize and maintain feature configurations for all roles
 * - Store subscription plan feature mappings
 * - Provide methods to query available features for a role
 * - Provide methods to query available features for subscription plans
 * - Support role-based and subscription-based feature accessibility queries
 *
 * Architecture:
 * - Three internal Maps store role/subscription configurations
 * - Initialized on service creation via initializeConfig()
 * - Features provided via getter methods returning Set<string> or array
 * - Used in conjunction with PolicyCheckerService for access decisions
 *
 * Data Structure:
 * - defaultFeaturesByRole: Features always accessible by a role
 * - subscriptionFeaturesByRole: Features unlockable via subscription for each role
 * - featuresBySubscription: Feature sets associated with each subscription plan
 *
 * @example
 * // Inject the service
 * constructor(private config: AccessControlConfigService) {}
 *
 * // Get features accessible without subscription
 * const defaultFeatures = this.config.getDefaultFeaturesForRole(Roles.FARMER);
 * // Returns: Set {'DASHBOARD', 'POND', 'FEED', 'DENSITY', ...}
 *
 * // Get features unlocked by active subscriptions
 * const activePlans = ['LITE', 'CORE'];
 * const subscriptionFeatures = this.config.getFeaturesByActivePolicyFlags(activePlans);
 * // Returns: Array of features available for those plans
 */
export class AccessControlConfigService {

    /**
     * @private
     * @type {Map<string, Set<string>>}
     * @description Maps user roles to their default features (accessible without subscription).
     * Features in this map are always available to users with the mapped role,
     * regardless of subscription status.
     */
    private defaultFeaturesByRole: Map<string, Set<string>> = new Map();

    /**
     * @private
     * @type {Map<string, Set<string>>}
     * @description Maps user roles to features that can be unlocked via subscriptions.
     * These features require an active subscription plan to be accessible,
     * even if the user's role would normally support them.
     */
    private subscriptionFeaturesByRole: Map<string, Set<string>> = new Map();

    /**
     * @private
     * @type {Map<string, Set<string>>}
     * @description Maps subscription policy flags to their associated features.
     * Each subscription plan (LITE, CORE, PREMIUM, etc.) has a set of features
     * it unlocks when the user has an active subscription.
     */
    private featuresBySubscription: Map<string, Set<string>> = new Map();

    /**
     * @constructor
     * @description Initializes the Access Control Configuration Service.
     * Automatically calls initializeConfig() to populate all feature mappings.
     */
    constructor() {
        this.initializeConfig();
    }

    /**
     * @method initializeConfig
     * @description Initializes the RBAC configuration by populating all feature mappings.
     * Called during service construction to set up:
     * - Default features for each role (features always accessible)
     * - Subscription-unlockable features for each role (features requiring subscription)
     * - Feature mappings for each subscription policy
     *
     * This method establishes the complete access control matrix that determines
     * which features are available to which roles and subscriptions.
     *
     * Configuration Sections:
     * 1. Default features (always available to role)
     * 2. Subscription features (available only with active subscription)
     * 3. Subscription policy feature mappings (which plans unlock which features)
     *
     * @private
     * @returns {void}
     */
    private initializeConfig() {

        // ============ SECTION 1: Default Features for Each Role ============
        // Features always accessible for that role (no subscription required)
        this.defaultFeaturesByRole.set(Roles.EMPLOYEE, new Set([
            ...this.superAdminFeatures
        ]));

        this.defaultFeaturesByRole.set(Roles.FARM_BUSINESS_ADMIN, new Set([
            ...this.commonFarmFeatures
        ]));

        this.defaultFeaturesByRole.set(Roles.FARMER, new Set([
            ...this.commonFarmFeatures
        ]));

        this.defaultFeaturesByRole.set(Roles.FARM_MANAGER, new Set([
            ...this.commonFarmFeatures
        ]));

        this.defaultFeaturesByRole.set(Roles.PROVIDER_BUSINESS_ADMIN, new Set([
            ...this.commonProviderBusinessFeatures,
            'YOUR_BUSINESS', 'PRODUCTS'
        ]));

        this.defaultFeaturesByRole.set(Roles.PROVIDER_MANAGER, new Set([
            ...this.commonProviderBusinessFeatures
        ]));

      this.defaultFeaturesByRole.set(Roles.CUSTOMER_SUPPORT, new Set([
        'MARKET_CONNECT', 'SUBSCRIPTIONS', 'BLUEYE'
      ]));

        // ============ SECTION 2: Subscription-Unlockable Features per Role ============
        // Features that can be unlocked if user has an active subscription
        this.subscriptionFeaturesByRole.set(Roles.EMPLOYEE, new Set([]));

        this.subscriptionFeaturesByRole.set(Roles.FARM_BUSINESS_ADMIN, new Set([]));

        this.subscriptionFeaturesByRole.set(Roles.FARMER, new Set([]));

        this.subscriptionFeaturesByRole.set(Roles.FARM_MANAGER, new Set([]));

        this.subscriptionFeaturesByRole.set(Roles.PROVIDER_BUSINESS_ADMIN, new Set([]));

        this.subscriptionFeaturesByRole.set(Roles.PROVIDER_MANAGER, new Set([]));

        this.subscriptionFeaturesByRole.set(Roles.CUSTOMER_SUPPORT, new Set([]));

        // ============ SECTION 3: Feature Mappings per Subscription Plan ============
        // Maps each subscription policy to the features it unlocks
        this.featuresBySubscription.set(Policies.TRIAL, new Set([]));

        this.featuresBySubscription.set(Policies.LITE, new Set([]));

        this.featuresBySubscription.set(Policies.CORE, new Set([]));

        this.featuresBySubscription.set(Policies.PREMIUM, new Set([]));

        this.featuresBySubscription.set(Policies.PRIORITY_PREMIUM, new Set([]));

        this.featuresBySubscription.set(Policies.BLUEYE, new Set([]));
    }

    /* ----------- Helper Methods ------------- */

    /**
     * @property commonFarmFeatures
     * @description Getter returning the common feature set available for all farm-related roles
     * (Business Admin, Farmer, Farm Manager).
     *
     * These features represent the baseline set that every farm role can access
     * without depending on subscription plans. Includes operational, management,
     * and reporting features specific to aquaculture farm operations.
     *
     * Feature Categories Included:
     * - Core Operations: DASHBOARD, POND, FEED, DENSITY, HARVEST, WATER_QUALITY
     * - Monitoring: SAMPLING, READINGS_HISTORY, ANALYTICS
     * - Inventory: PRODUCTS, VENDORS
     * - Financial: SPEND_ANALYSIS, MATERIAL_TRANSACTIONS, BATCH_TRANSACTIONS, PAYMENT_TRANSACTIONS
     * - Workforce: YOUR_FIELD_FORCE, TANK_SHIFT
     * - Insights: ACTUAL_VS_PROJECTED, CULTURE_SUMMARY, POND_SUMMARY, DAY_SUMMARY, WEEKLY_GROWTH
     * - Traceability: TRACEABILITY, MATERIAL_MOVEMENT, TRANSACTIONS, PAYMENTS
     * - Tools: SUBSCRIPTION_PLANS
     *
     * @type {string[]}
     * @returns {string[]} Array of feature identifiers available to farm roles
     *
     * @example
     * const farmFeatures = this.config.commonFarmFeatures;
     * // Returns: ['DASHBOARD', 'POND', 'FEED', 'DENSITY', 'HARVEST', ...]
     */
    public get commonFarmFeatures() {
        const features = [
            'DASHBOARD', 'POND', 'FEED', 'DENSITY', 'HARVEST', 'WATER_QUALITY', 'SAMPLING', 'READINGS_HISTORY',
            'PRODUCTS', 'VENDORS', 'SPEND_ANALYSIS', 'MATERIAL_TRANSACTIONS', 'BATCH_TRANSACTIONS', 'PAYMENT_TRANSACTIONS',
            'YOUR_BUSINESS', 'YOUR_FIELD_FORCE', 'TANK_SHIFT', 'ACTUAL_VS_PROJECTED', 'ANALYTICS', 'CULTURE_SUMMARY', 'POND_SUMMARY',
            'DAY_SUMMARY', 'WEEKLY_GROWTH', 'TRACEABILITY', 'MATERIAL_MOVEMENT', 'TRANSACTIONS', 'PAYMENTS',
            'SUBSCRIPTION_PLANS'
        ];
        return features;
    }

    /**
     * @property commonProviderBusinessFeatures
     * @description Getter returning the common feature set available for all provider business-related roles
     * (Business Admin, Provider Manager).
     *
     * These features represent the baseline set that every provider business role can access
     * without depending on subscription plans. Includes operational, customer management,
     * and service delivery features specific to provider business operations.
     *
     * Feature Categories Included:
     * - Core Operations: DASHBOARD, POND, FEED, DENSITY, HARVEST, WATER_QUALITY
     * - Customer Management: YOUR_CUSTOMERS, FARM_USERS, YOUR_FIELD_FORCE
     * - Monitoring: SAMPLING, READINGS_HISTORY, ANALYTICS
     * - Order Management: ORDERS
     * - Insights: CULTURE_SUMMARY, POND_SUMMARY, DAY_SUMMARY
     * - Traceability: TRACEABILITY
     * - Engagement: ACTIVITY_TRACKER_LEADS, VISITS
     *
     * @type {string[]}
     * @returns {string[]} Array of feature identifiers available to provider roles
     *
     * @example
     * const providerFeatures = this.config.commonProviderBusinessFeatures;
     * // Returns: ['DASHBOARD', 'YOUR_CUSTOMERS', 'FARM_USERS', 'POND', ...]
     */
    public get commonProviderBusinessFeatures() {
        const features = [
            'DASHBOARD', 'YOUR_CUSTOMERS', 'FARM_USERS', 'POND', 'FEED', 'DENSITY', 'HARVEST', 'WATER_QUALITY', 'SAMPLING', 'READINGS_HISTORY',
            'YOUR_FIELD_FORCE', 'ORDERS', 'CULTURE_SUMMARY', 'POND_SUMMARY', 'DAY_SUMMARY', 'TRACEABILITY',
            'ACTIVITY_TRACKER_LEADS', 'VISITS'

        ];
        return features;
    }

    /**
     * @property superAdminFeatures
     * @description Getter returning the comprehensive feature set available exclusively to the Super Admin role.
     *
     * This is the most complete feature set in the system, including all management,
     * configuration, operational, and administrative features. Goes beyond farm/provider-specific
     * features to cover system-wide setup, user management, policy configuration, and advanced
     * system administration capabilities.
     *
     * Feature Categories Included:
     * - Operations: DASHBOARD, POND, FEED, DENSITY, HARVEST, WATER_QUALITY, SAMPLING
     * - Configuration: ROUTES, FEATURES, SCREEN, SYNC
     * - Master Data: BUSINESS_CATEGORY, BUSINESS_TYPE, PERSON_CATEGORY, PERSON_ROLE,
     *   TRANSACTION_TYPE, UOM, ITEM_TYPE, SHAPES, DEVICE_MODEL, SENSOR
     * - Quality Management: SENSOR_TYPE, QUALITY_PARAMETERS_TYPES, QUALITY_PARAMETERS,
     *   TEST_CATEGORIES, QP_RANGES
     * - Business Setup: CULTURE_CATEGORIES, WATER_TYPE, SOIL_TYPE, SPECIES_TYPE,
     *   BLIND_FEED_SETTINGS, QUICK_REGISTRATION, BUSINESS_ENTITIES
     * - User Management: PEOPLE
     * - Operations: BARCODES, TANK_SHIFT, ACTIVITY_TRACKER_LEADS
     * - Inventory: PRODUCTS, VENDORS
     * - Financial: SPEND_ANALYSIS, MATERIAL_TRANSACTIONS, PAYMENT_TRANSACTIONS
     * - Insights: ANALYTICS, CULTURE_SUMMARY, POND_SUMMARY, DAY_SUMMARY, WEEKLY_GROWTH
     * - Traceability: TRACEABILITY, MATERIAL_MOVEMENT, TRANSACTIONS, PAYMENTS
     * - Tools: PRICE_TICKER_UPLOAD
     *
     * @type {string[]}
     * @returns {string[]} Array of all available feature identifiers in the system
     *
     * @example
     * const allFeatures = this.config.superAdminFeatures;
     * // Returns: ['DASHBOARD', 'POND', 'FEED', 'ROUTES', 'FEATURES', ...] (70+ features)
     */
    public get superAdminFeatures() {
        const features = [
            'DASHBOARD', 'POND', 'FEED', 'DENSITY', 'HARVEST', 'WATER_QUALITY', 'SAMPLING', 'READINGS_HISTORY', 'ROUTES', 'FEATURES', 'SCREEN', 'SYNC',
            'BUSINESS_CATEGORY', 'BUSINESS_TYPE', 'PERSON_ROLE', 'TRANSACTION_TYPE', 'UOM', 'ITEM_TYPE', 'SHAPES', 'DEVICE_MODEL', 'SENSOR', 'SENSOR_TYPE', 'QUALITY_PARAMETERS_TYPES',
            'QUALITY_PARAMETERS', 'TEST_CATEGORIES', 'QP_RANGES', 'ACTIVITIES_LIST', 'BROOD_BREEDERS', 'BROOD_LINES', 'OBSERVATION_TYPES', 'TREATMENT_TYPES', 'POND_TYPES', 'POND_USAGES', 'PRODUCTS', 'VENDORS',
            'SPEND_ANALYSIS', 'MATERIAL_TRANSACTIONS', 'PAYMENT_TRANSACTIONS', 'CULTURE_CATEGORIES', 'WATER_TYPE', 'SOIL_TYPE', 'SPECIES_TYPE',
            'QUICK_REGISTRATION', 'BUSINESS_ENTITIES', 'PEOPLE', 'BARCODES', 'TANK_SHIFT', 'ACTUAL_VS_PROJECTED', 'ANALYTICS', 'CULTURE_SUMMARY', 'POND_SUMMARY',
            'DAY_SUMMARY', 'WEEKLY_GROWTH', 'TRACEABILITY', 'MATERIAL_MOVEMENT', 'TRANSACTIONS', 'PAYMENTS', 'ACTIVITY_TRACKER_LEADS', 'PRICE_TICKER_UPLOAD'
        ];
        return features;
    }

    /**
     * @method getDefaultFeaturesForRole
     * @description Retrieves the set of features available to a user with the specified role
     * without requiring any subscription. These are the baseline features that are always
     * accessible based on role alone.
     *
     * Use this method to determine which features should be visible to a user who has
     * no active subscriptions or when checking role-based access without subscription context.
     *
     * @public
     * @param {string} role - The role identifier (from Roles enum) of the user.
     *   Example values: 'EMPLOYEE', 'Farm Business Admin', 'Farmer', 'Provider Business Admin'
     * @returns {Set<string>} Set of feature identifiers accessible by the role without subscription.
     *   Returns empty Set if role is not recognized.
     *
     * @example
     * // Get features for a farmer
     * const farmerFeatures = this.config.getDefaultFeaturesForRole(Roles.FARMER);
     * console.log(farmerFeatures);
     * // Output: Set(25) { 'DASHBOARD', 'POND', 'FEED', 'DENSITY', ... }
     *
     * // Check if feature is accessible
     * if (farmerFeatures.has('POND')) {
     *   // Show pond management feature
     * }
     */
    public getDefaultFeaturesForRole(role: string): Set<string> {
        return this.defaultFeaturesByRole.get(role) || new Set();
    }

    /**
     * @method getSubscriptionAllowedFeaturesForRole
     * @description Retrieves the set of features that can be unlocked for a user with the specified role
     * through active subscriptions. These features are not available by default but require
     * an active subscription plan matching the role.
     *
     * Use this method to determine which premium/additional features could be made available
     * to a user if they activate a subscription.
     *
     * @public
     * @param {string} role - The role identifier (from Roles enum) of the user.
     * @returns {Set<string>} Set of features unlockable via subscriptions for the given role.
     *   Returns empty Set if role is not recognized or has no subscription-unlockable features.
     *
     * @example
     * // Get subscription-unlockable features for a provider business admin
     * const subscriptionFeatures = this.config.getSubscriptionAllowedFeaturesForRole(
     *   Roles.PROVIDER_BUSINESS_ADMIN
     * );
     * // Use this to show users which premium features they could unlock with a subscription
     */
    public getSubscriptionAllowedFeaturesForRole(role: string): Set<string> {
        return this.subscriptionFeaturesByRole.get(role) || new Set();
    }

    /**
     * @method getFeaturesByActivePolicyFlags
     * @description Retrieves the combined set of features available based on a user's active
     * subscription plans. Looks up features for each provided subscription policy flag,
     * merges them into a single unique set, and returns as an array.
     *
     * This is particularly useful for determining the complete feature set a user can access
     * when they have multiple active subscriptions (e.g., both LITE and CORE plans).
     * All features from all active plans are merged together (union operation).
     *
     * Algorithm:
     * 1. Create an empty Set to accumulate features
     * 2. For each active policy flag provided
     * 3. Look up features from featuresBySubscription map
     * 4. Add all features to the accumulation set
     * 5. Convert set to array and return
     *
     * @public
     * @param {string[]} activePolicyFlags - Array of subscription policy identifiers.
     *   Valid values: 'TBC' (TRIAL), 'LITE', 'CORE', 'PBC' (PREMIUM), 'PPP' (PRIORITY_PREMIUM), 'BELP' (BLUEYE)
     *   Example: ['TBC', 'LITE'] or [Policies.LITE, Policies.CORE]
     * @returns {string[]} Array of unique feature identifiers available across all active plans.
     *   Returns empty array if no valid plans provided or plans have no features mapped.
     *
     * @example
     * // Get features for user with LITE and CORE subscriptions
     * const userPlans = ['LITE', 'CORE'];
     * const availableFeatures = this.config.getFeaturesByActivePolicyFlags(userPlans);
     * // Returns: ['FEATURE_1', 'FEATURE_2', ...]
     *
     * // Using enum values
     * const features = this.config.getFeaturesByActivePolicyFlags([
     *   Policies.LITE,
     *   Policies.PREMIUM
     * ]);
     *
     * // Check if a feature is available
     * if (availableFeatures.includes('ADVANCED_ANALYTICS')) {
     *   // Show advanced analytics feature
     * }
     */
    public getFeaturesByActivePolicyFlags(activePolicyFlags: string[]): string[] {
        const allFeatures = new Set<string>();

        activePolicyFlags.forEach(plan => {
            const features = this.featuresBySubscription.get(plan as Policies);
            if (features) {
                features.forEach(f => allFeatures.add(f));
            }
        });

        return Array.from(allFeatures);
    }

}
